import { useCallback, useEffect, useMemo } from "react"

import { useMatch, useRoutes } from "react-router-dom"

import {
    ImageCollectionLayer,
    ImageSelectedLayer,
    useImageCollectionDispatchContext,
} from "@l2r-front/l2r-images"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import {
    NetworkReferentialLayer,
    useNavigateToLinearLocation,
    useNetworksDispatchContext,
} from "@l2r-front/l2r-networks"

import { APP_SAFETY } from "../../../../common/constants/appSafety"
import { API_MODULE_SPEEDINGS, MODULE_SPEEDINGS } from "../../../../common/constants/modules"
import {
    SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER,
    SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER,
} from "../../constants/urlParams"
import { RoadSpeedingsLayer } from "../../containers/RoadSpeedingsLayer"
import { RoadTrafficLayer } from "../../containers/RoadTrafficLayer"
import { SpeedingsLayer } from "../../containers/SpeedingsLayer"
import { TrafficLayer } from "../../containers/TrafficLayer"
import { useSpeedingsStateContext } from "../../contexts/SpeedingsContext"

export function SpeedingsMapLayersRouter() {

    const match = useMatch(`:slugId/${APP_SAFETY}/${MODULE_SPEEDINGS}/:road/*`)
    const road = decodeURIComponent(match?.params?.road)
    const isSegmentView = !!match
    const { currentActiveLayer } = useSpeedingsStateContext()

    const navigateToLinearLocation = useNavigateToLinearLocation()
    const { setSegmentFeatureIdClicked } = useImageCollectionDispatchContext()
    const { setSearchRoadCallback } = useMapDispatchContext()
    const { setSelectedLinearLocations } = useNetworksDispatchContext()

    const processNavigation = useCallback((feature, location, navigationOrigin) => {
        if (location && location.road && location.road !== road) {
            setSegmentFeatureIdClicked(feature?.properties?.uuid)
            setSelectedLinearLocations(feature)
            navigateToLinearLocation(location, ".", { saveMapBoundingBox: !isSegmentView, navigationOrigin: navigationOrigin })
        }
    }, [setSegmentFeatureIdClicked, setSelectedLinearLocations, navigateToLinearLocation, isSegmentView, road])

    useEffect(() => {
        setSearchRoadCallback((searchedRoad) => {
            processNavigation(null, { road: searchedRoad })
        })

        return () => setSearchRoadCallback(null)
    }, [
        setSearchRoadCallback,
        processNavigation])

    const displayedLayer = useMemo(() => {
        if (currentActiveLayer === SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER || currentActiveLayer === SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER) {
            return <>
                <TrafficLayer />
                {isSegmentView && <RoadTrafficLayer road={road} />}
            </>
        } else {
            return <>
                <SpeedingsLayer processNavigation={processNavigation} roadToHide={road} />
                {isSegmentView && <RoadSpeedingsLayer road={road} />}
            </>
        }
    }, [currentActiveLayer, isSegmentView, processNavigation, road])

    const layers = useMemo(() => {
        return (
            <>
                <NetworkReferentialLayer module={API_MODULE_SPEEDINGS} processNavigation={processNavigation} roadToHide={road} />
                {displayedLayer}
                {isSegmentView && <ImageCollectionLayer road={road} />}
                {isSegmentView && <ImageSelectedLayer />}
            </>
        )
    }, [displayedLayer, isSegmentView, processNavigation, road])

    const layersRoutes = useMemo(() => ([
        { path: "/", element: layers },
        { path: "/:road", element: layers },
    ]), [layers])

    return useRoutes(layersRoutes)
}